import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import {
  PlusIcon,
  ChevronUpDownIcon,
  ChevronDownIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  BuildingLibraryIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  ArrowTopRightOnSquareIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { knowledgeBaseApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import { PageHeader, StatusBadge, StatCard } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

// ─── Constants ────────────────────────────────────────────────────────────────

const AGENCIES = [
  { value: 'CFPB', label: 'CFPB' },
  { value: 'OCC', label: 'OCC' },
  { value: 'FDIC', label: 'FDIC' },
  { value: 'NCUA', label: 'NCUA' },
  { value: 'FRB', label: 'FRB' },
  { value: 'FHFA', label: 'FHFA' },
  { value: 'SEC', label: 'SEC' },
  { value: 'FINRA', label: 'FINRA' },
  { value: 'STATE', label: 'State' },
];

const STATUSES = [
  { value: 'CURRENT', label: 'Current' },
  { value: 'PROPOSED', label: 'Proposed' },
  { value: 'AMENDED', label: 'Amended' },
  { value: 'REPEALED', label: 'Repealed' },
];

const COMPLIANCE_AREAS = [
  { value: 'HMDA', label: 'HMDA' },
  { value: 'FAIR_LENDING', label: 'Fair Lending' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'BSA_AML', label: 'BSA/AML' },
  { value: 'DEPOSITS', label: 'Deposits' },
  { value: 'PRIVACY', label: 'Privacy' },
  { value: 'LENDING', label: 'Lending' },
  { value: 'UDAAP', label: 'UDAAP' },
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'COMMUNITY', label: 'Community' },
];

const PRODUCT_TYPES = [
  { value: 'MORTGAGE', label: 'Mortgage' },
  { value: 'AUTO_LOAN', label: 'Auto Loan' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'PERSONAL_LOAN', label: 'Personal Loan' },
  { value: 'CHECKING', label: 'Checking' },
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'CD', label: 'CD' },
  { value: 'HELOC', label: 'HELOC' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'OTHER', label: 'Other' },
];

const AGENCY_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  CFPB: { label: 'CFPB', variant: 'info' },
  OCC: { label: 'OCC', variant: 'warning' },
  FDIC: { label: 'FDIC', variant: 'success' },
  NCUA: { label: 'NCUA', variant: 'neutral' },
  FRB: { label: 'FRB', variant: 'draft' },
  FHFA: { label: 'FHFA', variant: 'neutral' },
  SEC: { label: 'SEC', variant: 'neutral' },
  FINRA: { label: 'FINRA', variant: 'neutral' },
  STATE: { label: 'State', variant: 'neutral' },
};

const STATUS_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  CURRENT: { label: 'Current', variant: 'success' },
  PROPOSED: { label: 'Proposed', variant: 'info' },
  AMENDED: { label: 'Amended', variant: 'warning' },
  REPEALED: { label: 'Repealed', variant: 'danger' },
};

const AREA_LABEL: Record<string, string> = Object.fromEntries(
  COMPLIANCE_AREAS.map((a) => [a.value, a.label]),
);

const EMPTY_FORM = {
  commonName: '',
  fullTitle: '',
  agency: 'CFPB',
  cfrTitle: '',
  cfrPart: '',
  cfrSection: '',
  summary: '',
  complianceAreas: [] as string[],
  applicableProductTypes: [] as string[],
  sourceUrl: '',
  effectiveDate: '',
};

// ─── Filter dropdown helper ───────────────────────────────────────────────────

function FilterListbox({
  value,
  onChange,
  options,
  placeholder,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  className?: string;
}) {
  const allOptions = [{ value: '', label: placeholder }, ...options];
  const selectedLabel = allOptions.find((o) => o.value === value)?.label || placeholder;

  return (
    <Listbox value={value} onChange={onChange}>
      <div className={clsx('relative', className)}>
        <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
          <span className="block truncate">{selectedLabel}</span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {allOptions.map((o) => (
              <Listbox.Option
                key={o.value}
                value={o.value}
                className={({ active }) =>
                  clsx(
                    'relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                    active ? 'bg-teal-50 text-teal-700' : 'text-gray-900',
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <span className={clsx('block truncate', selected && 'font-medium')}>
                      {o.label}
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                        <CheckIcon className="h-4 w-4" />
                      </span>
                    )}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

// ─── Multi-select listbox helper ──────────────────────────────────────────────

function MultiSelectListbox({
  values,
  onChange,
  options,
  label,
  className,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  options: { value: string; label: string }[];
  label: string;
  className?: string;
}) {
  const toggle = (v: string) => {
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  };

  return (
    <Listbox value={values} onChange={() => {}} multiple>
      <div className={clsx('relative', className)}>
        <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
          <span className="block truncate">
            {values.length > 0
              ? values.map((v) => options.find((o) => o.value === v)?.label || v).join(', ')
              : label}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {options.map((o) => (
              <Listbox.Option
                key={o.value}
                value={o.value}
                className={({ active }) =>
                  clsx(
                    'relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                    active ? 'bg-teal-50 text-teal-700' : 'text-gray-900',
                  )
                }
                onClick={() => toggle(o.value)}
              >
                <>
                  <span
                    className={clsx(
                      'block truncate',
                      values.includes(o.value) && 'font-medium',
                    )}
                  >
                    {o.label}
                  </span>
                  {values.includes(o.value) && (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-teal-600">
                      <CheckIcon className="h-4 w-4" />
                    </span>
                  )}
                </>
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

// ─── Page component ───────────────────────────────────────────────────────────

export default function RegulatoryKnowledgeBasePage() {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();

  // State
  const [search, setSearch] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 25;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [noteText, setNoteText] = useState('');

  // ─── Queries ──────────────────────────────────────────────────────────────

  const { data: listData, isLoading } = useQuery({
    queryKey: ['regulations', teamId, search, agencyFilter, statusFilter, areaFilter, page],
    queryFn: () =>
      knowledgeBaseApi.list(teamId!, {
        search: search || undefined,
        agency: agencyFilter || undefined,
        status: statusFilter || undefined,
        complianceArea: areaFilter || undefined,
        limit,
        offset: page * limit,
      }),
    enabled: !!teamId,
  });
  const regulations = listData?.data?.regulations || [];
  const total = listData?.data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const { data: dashData } = useQuery({
    queryKey: ['regulationsDashboard', teamId],
    queryFn: () => knowledgeBaseApi.dashboard(teamId!),
    enabled: !!teamId,
  });
  const dashboard = dashData?.data;

  const { data: detailData } = useQuery({
    queryKey: ['regulation', teamId, expandedId],
    queryFn: () => knowledgeBaseApi.get(teamId!, expandedId!),
    enabled: !!teamId && !!expandedId,
  });
  const expandedReg = detailData?.data;

  // ─── Mutations ────────────────────────────────────────────────────────────

  const seedMutation = useMutation({
    mutationFn: () => knowledgeBaseApi.seed(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulations'] });
      queryClient.invalidateQueries({ queryKey: ['regulationsDashboard'] });
      toast.success('Regulatory knowledge base seeded successfully');
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Failed to seed regulations'),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) =>
      editingId
        ? knowledgeBaseApi.update(teamId!, editingId, data)
        : knowledgeBaseApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulations'] });
      queryClient.invalidateQueries({ queryKey: ['regulationsDashboard'] });
      toast.success(editingId ? 'Regulation updated' : 'Regulation created');
      closeModal();
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Failed to save regulation'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => knowledgeBaseApi.delete(teamId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulations'] });
      queryClient.invalidateQueries({ queryKey: ['regulationsDashboard'] });
      toast.success('Regulation deleted');
      if (expandedId) setExpandedId(null);
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Failed to delete regulation'),
  });

  const addNoteMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      knowledgeBaseApi.addNote(teamId!, id, { text }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulation', teamId, expandedId] });
      queryClient.invalidateQueries({ queryKey: ['regulationsDashboard'] });
      toast.success('Note added');
      setNoteText('');
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Failed to add note'),
  });

  const deleteNoteMutation = useMutation({
    mutationFn: ({ regId, noteId }: { regId: string; noteId: string }) =>
      knowledgeBaseApi.deleteNote(teamId!, regId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regulation', teamId, expandedId] });
      queryClient.invalidateQueries({ queryKey: ['regulationsDashboard'] });
      toast.success('Note deleted');
    },
    onError: (err: any) =>
      toast.error(err.response?.data?.error || 'Failed to delete note'),
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (reg: any) => {
    setEditingId(reg.id);
    setForm({
      commonName: reg.commonName || '',
      fullTitle: reg.fullTitle || '',
      agency: reg.agency || 'CFPB',
      cfrTitle: reg.cfrTitle || '',
      cfrPart: reg.cfrPart || '',
      cfrSection: reg.cfrSection || '',
      summary: reg.summary || '',
      complianceAreas: reg.complianceAreas || [],
      applicableProductTypes: reg.applicableProductTypes || [],
      sourceUrl: reg.sourceUrl || '',
      effectiveDate: reg.effectiveDate ? reg.effectiveDate.slice(0, 10) : '',
    });
    setShowModal(true);
  };

  const handleSubmit = () => {
    if (!form.commonName.trim()) {
      toast.error('Common name is required');
      return;
    }
    createMutation.mutate({
      ...form,
      effectiveDate: form.effectiveDate || undefined,
    });
  };

  const toggleRow = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
    setNoteText('');
  };

  const resetFilters = () => {
    setSearch('');
    setAgencyFilter('');
    setStatusFilter('');
    setAreaFilter('');
    setPage(0);
  };

  const hasActiveFilters = search || agencyFilter || statusFilter || areaFilter;

  // ─── Derived stats ────────────────────────────────────────────────────────

  const totalRegulations = dashboard?.total ?? dashboard?.totalRegulations ?? 0;
  const topAgencies = dashboard?.byAgency
    ? (Array.isArray(dashboard.byAgency)
        ? (dashboard.byAgency as { agency: string; count: number }[])
            .sort((a, b) => b.count - a.count)
            .slice(0, 3)
            .map((item) => [item.agency, item.count] as [string, number])
        : Object.entries(dashboard.byAgency as Record<string, number>)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3))
    : [];
  const recentlyAmended = dashboard?.recentlyAmended ?? 0;
  const totalNotes = dashboard?.totalNotes ?? 0;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <FeatureGate feature="regulatory_knowledge_base" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Regulatory Knowledge Base"
          description="Browse, search, and annotate federal and state regulations applicable to your institution."
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <>
                <button
                  onClick={() => seedMutation.mutate()}
                  disabled={seedMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                  title="Load 29 hand-curated federal regulations (CFPB, OCC, FDIC, NCUA, FRB)"
                >
                  <ServerStackIcon className="h-4 w-4" />
                  {seedMutation.isPending ? 'Loading...' : 'Load Starter Library'}
                </button>
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Custom Regulation
                </button>
              </>
            </PermissionGate>
          }
        />

        {/* Stats row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={BookOpenIcon}
            label="Total Regulations"
            value={totalRegulations}
          />
          <StatCard
            icon={BuildingLibraryIcon}
            label="By Agency (Top 3)"
            value={
              topAgencies.length > 0
                ? topAgencies.map(([ag, ct]) => `${ag}: ${ct}`).join(', ')
                : '--'
            }
          />
          <StatCard
            icon={ClockIcon}
            label="Recently Amended (90d)"
            value={recentlyAmended}
          />
          <StatCard
            icon={ChatBubbleLeftIcon}
            label="Team Notes"
            value={totalNotes}
          />
        </div>

        {/* Search + Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or CFR part..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-teal-500 focus:ring-teal-500"
            />
          </div>
          <FilterListbox
            value={agencyFilter}
            onChange={(v) => {
              setAgencyFilter(v);
              setPage(0);
            }}
            options={AGENCIES}
            placeholder="All Agencies"
            className="w-40"
          />
          <FilterListbox
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(0);
            }}
            options={STATUSES}
            placeholder="All Statuses"
            className="w-40"
          />
          <FilterListbox
            value={areaFilter}
            onChange={(v) => {
              setAreaFilter(v);
              setPage(0);
            }}
            options={COMPLIANCE_AREAS}
            placeholder="All Areas"
            className="w-44"
          />
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Results Table */}
        <div className="rounded-lg border bg-white shadow-sm">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-600 border-t-transparent" />
            </div>
          ) : regulations.length === 0 ? (
            <div className="py-16 text-center px-6">
              <BookOpenIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-3 text-base font-semibold text-gray-900">
                {hasActiveFilters ? 'No regulations match your filters' : 'Your knowledge base is empty'}
              </p>
              {hasActiveFilters ? (
                <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters.</p>
              ) : (
                <>
                  <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                    Load Clarid's starter library of 29 hand-curated federal regulations
                    (CFPB, OCC, FDIC, NCUA, FRB) covering Reg B, C, X, Z, BSA, ECOA, RESPA,
                    TILA, HMDA, CRA, and more. You can edit, annotate, and add your own
                    regulations on top.
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => seedMutation.mutate()}
                      disabled={seedMutation.isPending}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
                    >
                      <ServerStackIcon className="h-4 w-4" />
                      {seedMutation.isPending ? 'Loading...' : 'Load Starter Library (29 regulations)'}
                    </button>
                    <button
                      onClick={openCreate}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Custom Regulation
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Common Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      CFR Reference
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Agency
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Compliance Areas
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Products
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {regulations.map((reg: any) => {
                    const isExpanded = expandedId === reg.id;
                    const agBadge = AGENCY_BADGE[reg.agency] || {
                      label: reg.agency,
                      variant: 'neutral' as BadgeVariant,
                    };
                    const stBadge = STATUS_BADGE[reg.status] || {
                      label: reg.status,
                      variant: 'neutral' as BadgeVariant,
                    };
                    const areas: string[] = reg.complianceAreas || [];
                    const displayedAreas = areas.slice(0, 3);
                    const remainingAreas = areas.length - 3;

                    return (
                      <Fragment key={reg.id}>
                        <tr
                          onClick={() => toggleRow(reg.id)}
                          className={clsx(
                            'cursor-pointer transition-colors',
                            isExpanded
                              ? 'bg-teal-50'
                              : 'hover:bg-gray-50',
                          )}
                        >
                          <td className="whitespace-nowrap px-4 py-3">
                            <div className="flex items-center gap-2">
                              <ChevronDownIcon
                                className={clsx(
                                  'h-4 w-4 text-gray-400 transition-transform',
                                  isExpanded && 'rotate-180',
                                )}
                              />
                              <span className="text-sm font-medium text-navy-900">
                                {reg.commonName}
                              </span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                            {reg.cfrTitle && reg.cfrPart
                              ? `${reg.cfrTitle} CFR ${reg.cfrPart}${reg.cfrSection ? '.' + reg.cfrSection : ''}`
                              : '--'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <StatusBadge variant={agBadge.variant}>
                              {agBadge.label}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {displayedAreas.map((a) => (
                                <span
                                  key={a}
                                  className="inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700"
                                >
                                  {AREA_LABEL[a] || a}
                                </span>
                              ))}
                              {remainingAreas > 0 && (
                                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                                  +{remainingAreas}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <StatusBadge variant={stBadge.variant}>
                              {stBadge.label}
                            </StatusBadge>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                            {reg.applicableProductTypes?.length || 0}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                            {reg.notesCount ?? reg.notes?.length ?? 0}
                          </td>
                        </tr>

                        {/* Expanded detail row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="bg-gray-50 px-0 py-0">
                              <div className="border-t border-gray-200 px-6 py-5 space-y-5">
                                {/* Full title */}
                                {(expandedReg?.fullTitle || reg.fullTitle) && (
                                  <div>
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                      Full Title
                                    </h4>
                                    <p className="mt-1 text-sm text-gray-800">
                                      {expandedReg?.fullTitle || reg.fullTitle}
                                    </p>
                                  </div>
                                )}

                                {/* Summary */}
                                {(expandedReg?.summary || reg.summary) && (
                                  <div>
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                      Summary
                                    </h4>
                                    <p className="mt-1 text-sm text-gray-700 leading-relaxed">
                                      {expandedReg?.summary || reg.summary}
                                    </p>
                                  </div>
                                )}

                                {/* Key Requirements */}
                                {(expandedReg?.keyRequirements || reg.keyRequirements)?.length > 0 && (
                                  <div>
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                      Key Requirements
                                    </h4>
                                    <ul className="mt-1 list-disc pl-5 space-y-1">
                                      {(expandedReg?.keyRequirements || reg.keyRequirements).map(
                                        (req: string, i: number) => (
                                          <li key={i} className="text-sm text-gray-700">
                                            {req}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                )}

                                {/* Source URL */}
                                {(expandedReg?.sourceUrl || reg.sourceUrl) && (
                                  <div>
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                      Source
                                    </h4>
                                    <a
                                      href={expandedReg?.sourceUrl || reg.sourceUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mt-1 inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {expandedReg?.sourceUrl || reg.sourceUrl}
                                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                    </a>
                                  </div>
                                )}

                                {/* Actions */}
                                <div className="flex items-center gap-3 border-t border-gray-200 pt-4">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEdit(expandedReg || reg);
                                    }}
                                    className="text-sm font-medium text-teal-600 hover:text-teal-700"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (confirm('Delete this regulation? This cannot be undone.')) {
                                        deleteMutation.mutate(reg.id);
                                      }
                                    }}
                                    className="text-sm font-medium text-red-600 hover:text-red-700"
                                  >
                                    Delete
                                  </button>
                                </div>

                                {/* Team Notes section */}
                                <div className="border-t border-gray-200 pt-4">
                                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    Team Notes
                                  </h4>
                                  {/* Existing notes */}
                                  {expandedReg?.notes?.length > 0 ? (
                                    <div className="mt-2 space-y-2">
                                      {expandedReg.notes.map((note: any) => (
                                        <div
                                          key={note.id}
                                          className="flex items-start justify-between rounded-lg bg-white p-3 border"
                                        >
                                          <div className="min-w-0 flex-1">
                                            <p className="text-sm text-gray-700">{note.text}</p>
                                            <p className="mt-1 text-xs text-gray-400">
                                              {note.createdBy?.name || 'Team member'} &middot;{' '}
                                              {new Date(note.createdAt).toLocaleDateString()}
                                            </p>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteNoteMutation.mutate({
                                                regId: reg.id,
                                                noteId: note.id,
                                              });
                                            }}
                                            className="ml-2 shrink-0 text-gray-400 hover:text-red-500"
                                          >
                                            <XMarkIcon className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="mt-2 text-sm text-gray-400">No notes yet.</p>
                                  )}
                                  {/* Add note form */}
                                  <div className="mt-3 flex gap-2">
                                    <textarea
                                      rows={2}
                                      value={noteText}
                                      onChange={(e) => setNoteText(e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="Add a note..."
                                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!noteText.trim()) return;
                                        addNoteMutation.mutate({
                                          id: reg.id,
                                          text: noteText.trim(),
                                        });
                                      }}
                                      disabled={!noteText.trim() || addNoteMutation.isPending}
                                      className="self-end rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                                    >
                                      {addNoteMutation.isPending ? 'Adding...' : 'Add Note'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <p className="text-sm text-gray-500">
                Showing {page * limit + 1}&ndash;{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                  className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ─── Create / Edit Modal ───────────────────────────────────────────── */}
        <Transition appear show={showModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/40" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform rounded-xl bg-white p-6 shadow-xl transition-all">
                    <div className="flex items-center justify-between">
                      <Dialog.Title className="text-lg font-semibold text-navy-900">
                        {editingId ? 'Edit Regulation' : 'Add Custom Regulation'}
                      </Dialog.Title>
                      <button
                        onClick={closeModal}
                        className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="mt-5 space-y-4">
                      {/* Common Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Common Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.commonName}
                          onChange={(e) => setForm({ ...form, commonName: e.target.value })}
                          placeholder="e.g., Regulation Z"
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Full Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Full Title
                        </label>
                        <input
                          type="text"
                          value={form.fullTitle}
                          onChange={(e) => setForm({ ...form, fullTitle: e.target.value })}
                          placeholder="e.g., Truth in Lending Act (Regulation Z)"
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Agency + CFR row */}
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Agency</label>
                          <FilterListbox
                            value={form.agency}
                            onChange={(v) => setForm({ ...form, agency: v })}
                            options={AGENCIES}
                            placeholder="Select"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            CFR Title
                          </label>
                          <input
                            type="text"
                            value={form.cfrTitle}
                            onChange={(e) => setForm({ ...form, cfrTitle: e.target.value })}
                            placeholder="12"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            CFR Part
                          </label>
                          <input
                            type="text"
                            value={form.cfrPart}
                            onChange={(e) => setForm({ ...form, cfrPart: e.target.value })}
                            placeholder="1026"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            CFR Section
                          </label>
                          <input
                            type="text"
                            value={form.cfrSection}
                            onChange={(e) => setForm({ ...form, cfrSection: e.target.value })}
                            placeholder="36"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>

                      {/* Summary */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Summary</label>
                        <textarea
                          rows={3}
                          value={form.summary}
                          onChange={(e) => setForm({ ...form, summary: e.target.value })}
                          placeholder="Brief description of the regulation..."
                          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                        />
                      </div>

                      {/* Compliance Areas */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Compliance Areas
                        </label>
                        <MultiSelectListbox
                          values={form.complianceAreas}
                          onChange={(v) => setForm({ ...form, complianceAreas: v })}
                          options={COMPLIANCE_AREAS}
                          label="Select areas..."
                          className="mt-1"
                        />
                      </div>

                      {/* Product Types */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Applicable Product Types
                        </label>
                        <MultiSelectListbox
                          values={form.applicableProductTypes}
                          onChange={(v) => setForm({ ...form, applicableProductTypes: v })}
                          options={PRODUCT_TYPES}
                          label="Select products..."
                          className="mt-1"
                        />
                      </div>

                      {/* Source URL + Effective Date */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Source URL
                          </label>
                          <input
                            type="url"
                            value={form.sourceUrl}
                            onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })}
                            placeholder="https://..."
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Effective Date
                          </label>
                          <input
                            type="date"
                            value={form.effectiveDate}
                            onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Modal actions */}
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={closeModal}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={createMutation.isPending}
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                      >
                        {createMutation.isPending
                          ? 'Saving...'
                          : editingId
                            ? 'Update Regulation'
                            : 'Create Regulation'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
